import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { exportTableToCSV } from '@/utils/exportToCSV';
import { 
  BarChart3, 
  Download, 
  FileText, 
  TrendingUp, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Users,
  Calendar
} from 'lucide-react';

export default function ReportsPage() {
  const { organization } = useAuth();

  const { data: complianceStats, isLoading: statsLoading } = useQuery({
    queryKey: ['compliance-stats', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;

      const [controlsResult, risksResult, incidentsResult, suppliersResult, standardsResult] = await Promise.all([
        supabase.from('controls').select('status').eq('org_id', organization.id),
        supabase.from('risks').select('status, risk_score').eq('org_id', organization.id),
        supabase.from('incidents').select('status, priority').eq('org_id', organization.id),
        supabase.from('suppliers').select('criticality').eq('org_id', organization.id),
        supabase.from('standards').select('enabled').eq('org_id', organization.id),
      ]);

      const controls = controlsResult.data || [];
      const risks = risksResult.data || [];
      const incidents = incidentsResult.data || [];
      const suppliers = suppliersResult.data || [];
      const standards = standardsResult.data || [];

      return {
        controls: {
          total: controls.length,
          implemented: controls.filter(c => c.status === 'implemented').length,
          notImplemented: controls.filter(c => c.status === 'not_implemented').length,
          partiallyImplemented: controls.filter(c => c.status === 'partially_implemented').length,
        },
        risks: {
          total: risks.length,
          high: risks.filter(r => r.risk_score && r.risk_score >= 15).length,
          medium: risks.filter(r => r.risk_score && r.risk_score >= 10 && r.risk_score < 15).length,
          low: risks.filter(r => r.risk_score && r.risk_score < 10).length,
        },
        incidents: {
          total: incidents.length,
          open: incidents.filter(i => i.status !== 'resolved' && i.status !== 'closed').length,
          resolved: incidents.filter(i => i.status === 'resolved' || i.status === 'closed').length,
          critical: incidents.filter(i => i.priority === 'critical').length,
        },
        suppliers: {
          total: suppliers.length,
          critical: suppliers.filter(s => s.criticality === 'critical').length,
          high: suppliers.filter(s => s.criticality === 'high').length,
        },
        standards: {
          total: standards.length,
          enabled: standards.filter(s => s.enabled).length,
        }
      };
    },
    enabled: !!organization?.id,
  });

  const complianceScore = (complianceStats?.controls?.total || 0) > 0 
    ? Math.round(((complianceStats?.controls?.implemented || 0) / (complianceStats?.controls?.total || 1)) * 100)
    : 0;

  const generateReport = (type: string) => {
    toast.success(`Generating ${type} report...`);
    console.log(`Generating ${type} report...`);
  };

  const handleExportAllData = async () => {
    try {
      if (!organization?.id) {
        toast.error('No organization found');
        return;
      }

      toast.info('Preparing export...');

      const [controls, risks, incidents, suppliers, standards] = await Promise.all([
        supabase.from('controls').select('*').eq('org_id', organization.id),
        supabase.from('risks').select('*').eq('org_id', organization.id),
        supabase.from('incidents').select('*').eq('org_id', organization.id),
        supabase.from('suppliers').select('*').eq('org_id', organization.id),
        supabase.from('standards').select('*').eq('org_id', organization.id),
      ]);

      const exportData = {
        exported_at: new Date().toISOString(),
        organization: organization.name,
        data: {
          controls: controls.data || [],
          risks: risks.data || [],
          incidents: incidents.data || [],
          suppliers: suppliers.data || [],
          standards: standards.data || [],
        },
        statistics: complianceStats,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportAllToCSV = async () => {
    if (!organization?.id) {
      toast.error('No organization found');
      return;
    }

    try {
      toast.info('Exporting all tables to CSV...');

      const tables = [
        'sites', 'zones', 'systems', 'endpoints', 'risks', 
        'controls', 'incidents', 'suppliers', 'evidence', 
        'tasks', 'cloud_services', 'conduits', 'links',
        'access_relations', 'asset_relations', 'site_cloud_links',
        'shared_infrastructure', 'system_infrastructure_links'
      ] as const;

      let exportedCount = 0;
      
      for (const table of tables) {
        const { data, error } = await supabase
          .from(table as any)
          .select('*')
          .eq('org_id', organization.id);

        if (error) {
          console.warn(`Error fetching ${table}:`, error);
          continue;
        }

        if (data && data.length > 0) {
          exportTableToCSV(data, table);
          exportedCount++;
        }
      }

      toast.success(`Exported ${exportedCount} tables as CSV files`);
    } catch (error) {
      console.error('CSV Export error:', error);
      toast.error('Failed to export CSV files');
    }
  };

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Reports</h1>
            <p className="text-muted-foreground">Generate and view compliance reports</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground">Generate and view compliance reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={handleExportAllData}>
            <Download className="h-4 w-4" />
            Export JSON
          </Button>
          <Button className="gap-2" onClick={handleExportAllToCSV}>
            <Download className="h-4 w-4" />
            Export CSV (Excel)
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Compliance Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{complianceScore}%</div>
            <Progress value={complianceScore} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {complianceStats?.controls.implemented || 0} of {complianceStats?.controls.total || 0} controls
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              High Risks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{complianceStats?.risks.high || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {complianceStats?.risks.total || 0} total risks
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />
              Open Incidents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{complianceStats?.incidents.open || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {complianceStats?.incidents.total || 0} total incidents
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-accent" />
              Critical Suppliers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{complianceStats?.suppliers.critical || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {complianceStats?.suppliers.total || 0} total suppliers
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="risks">Risk Assessment</TabsTrigger>
          <TabsTrigger value="incidents">Incident Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Control Implementation Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-compliant" />
                    <span className="text-sm">Implemented</span>
                  </div>
                  <Badge className="bg-compliant text-compliant-foreground">
                    {complianceStats?.controls.implemented || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-warning" />
                    <span className="text-sm">Partially Implemented</span>
                  </div>
                  <Badge className="bg-warning text-warning-foreground">
                    {complianceStats?.controls.partiallyImplemented || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <span className="text-sm">Not Implemented</span>
                  </div>
                  <Badge className="bg-destructive text-destructive-foreground">
                    {complianceStats?.controls.notImplemented || 0}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Risk Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-destructive"></div>
                    <span className="text-sm">High Risk</span>
                  </div>
                  <Badge variant="destructive">{complianceStats?.risks.high || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-warning"></div>
                    <span className="text-sm">Medium Risk</span>
                  </div>
                  <Badge className="bg-warning text-warning-foreground">{complianceStats?.risks.medium || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-compliant"></div>
                    <span className="text-sm">Low Risk</span>
                  </div>
                  <Badge className="bg-compliant text-compliant-foreground">{complianceStats?.risks.low || 0}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Compliance Report</CardTitle>
                <CardDescription>
                  Overall compliance status and control implementation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => generateReport('compliance')} className="w-full gap-2">
                  <FileText className="h-4 w-4" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Standards Report</CardTitle>
                <CardDescription>
                  Detailed analysis of enabled compliance standards
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => generateReport('standards')} className="w-full gap-2">
                  <Shield className="h-4 w-4" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Gap Analysis</CardTitle>
                <CardDescription>
                  Identify compliance gaps and remediation actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => generateReport('gaps')} className="w-full gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="risks" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Risk Register</CardTitle>
                <CardDescription>
                  Complete list of identified organizational risks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => generateReport('risk-register')} className="w-full gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Risk Assessment</CardTitle>
                <CardDescription>
                  Risk analysis with impact and likelihood ratings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => generateReport('risk-assessment')} className="w-full gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Risk Treatment</CardTitle>
                <CardDescription>
                  Risk mitigation strategies and action plans
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => generateReport('risk-treatment')} className="w-full gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="incidents" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Incident Summary</CardTitle>
                <CardDescription>
                  Overview of all security incidents and their status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => generateReport('incident-summary')} className="w-full gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Incident Timeline</CardTitle>
                <CardDescription>
                  Chronological view of incidents and response times
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => generateReport('incident-timeline')} className="w-full gap-2">
                  <Calendar className="h-4 w-4" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}